import type { Dispatch } from 'react';
import React, { useEffect, useState } from 'react';
import { Subscribe } from 'unstated';
import { defineMessages, useIntl } from 'react-intl';
import UAParser from 'ua-parser-js';

import Link from '@atlaskit/link/link';

import { BannerStateContainer } from '@confluence/banners/entry-points/BannerStateContainer.ts';
import { Attribution } from '@confluence/error-boundary/entry-points/Attribution.ts';
import { ErrorBoundary } from '@confluence/error-boundary/entry-points/ErrorBoundary.ts';
import { DismissibleBanner } from '@confluence/banners/entry-points/DismissibleBanner.tsx';
import { confluenceSessionStorageInstance as sessionStorage } from '@confluence/storage-manager/entry-points/storageInstance.ts';
import { keys as storageKeys } from '@confluence/storage-manager/entry-points/LocalStorageKeys.ts';
import { getAnalyticsWebClient } from '@confluence/analytics-web-client';
import { isNativeMobile, setUserAgent } from '@confluence/browser-helper';

import { showBrowserRuntimeWarning } from '@devtools/browser-check';
import { addUFOCustomData } from '@atlaskit/react-ufo/custom-data';

const BANNER_HEIGHT = 8 * 9; // 72px when gridSize = 8px
const BANNER_NAME = 'UnsupportedBrowserBanner';

const messages = defineMessages({
	bannerMessage: {
		id: 'unsupported-browser-banner.banner-message',
		defaultMessage:
			'Your browser is <link>unsupported</link>. Switch or upgrade to maintain optimal performance.',
		description:
			'This is a warning message shown in a dismissible banner at the top of the page. The placeholder <link> wraps the word "unsupported" and links to a page listing supported browsers. It warns users that their browser version may cause performance issues and encourages them to switch or upgrade.',
	},
	bannerLink: {
		id: 'unsupported-browser-banner.banner-link',
		defaultMessage: 'unsupported',
		description:
			'This is the text displayed as a clickable link within the unsupported browser warning banner. After a user clicks on the link, it will open a page listing all supported browsers for Atlassian cloud products.',
	},
});

const BannerLink = (str: React.ReactNode[]) => (
	<Link
		href="https://support.atlassian.com/atlassian-account/docs/supported-browsers-for-atlassian-cloud-products/"
		target="_blank"
		rel="noreferrer noopener"
	>
		{str}
	</Link>
);

type BannerComponentProps = {
	bannerState: BannerStateContainer;
	setIsBannerClosed: Dispatch<boolean>;
	isBannerClosed: boolean;
};

const UnsupportedBrowserBannerComponent = ({
	bannerState,
	setIsBannerClosed,
	isBannerClosed,
}: BannerComponentProps) => {
	const intl = useIntl();

	useEffect(() => {
		const bannerClosedByUser = sessionStorage.getItemAsBoolean(
			storageKeys.BROWSER_BANNER_CLOSED_BY_USER,
		);
		if (bannerClosedByUser) {
			setIsBannerClosed(true);
			bannerState.hide(BANNER_NAME);
		}
	}, [setIsBannerClosed, isBannerClosed, bannerState]);

	return (
		<DismissibleBanner
			height={BANNER_HEIGHT}
			isWarning
			onDismiss={() => {
				bannerState.hide(BANNER_NAME);
				setIsBannerClosed(true);
				sessionStorage.setItem(storageKeys.BROWSER_BANNER_CLOSED_BY_USER, true);
			}}
		>
			{intl.formatMessage(messages.bannerMessage, {
				link: BannerLink,
			})}
		</DismissibleBanner>
	);
};

export const UnsupportedBrowserBanner = () => {
	const [isBannerClosed, setIsBannerClosed] = useState<boolean>(true);
	const failedFullCapabilitiesBrowserCheck = showBrowserRuntimeWarning();

	// Must call this before any browser-helper functions, to ensure user agent has been set first
	useEffect(setUserAgent, []);

	// Display banner if full browser capabilities check failed
	useEffect(() => {
		if (failedFullCapabilitiesBrowserCheck) {
			setIsBannerClosed(false);
		}
	}, [failedFullCapabilitiesBrowserCheck, setIsBannerClosed]);

	const userAgentParser = new UAParser(navigator.userAgent);
	const { name: browserName, version: browserVersion } = userAgentParser.getBrowser();

	// collect analytics
	useEffect(() => {
		void getAnalyticsWebClient().then((client) => {
			client.sendOperationalEvent({
				source: 'unsupportedBrowserBanner',
				action: 'executed',
				actionSubject: 'browserRuntimeCheck',
				attributes: {
					failedFullBrowserCheck: failedFullCapabilitiesBrowserCheck,
					browserName,
					browserVersion,
				},
			});
		});
	}, [failedFullCapabilitiesBrowserCheck, browserName, browserVersion]);

	if (
		isBannerClosed ||
		!failedFullCapabilitiesBrowserCheck ||
		isNativeMobile() // Do not render the banner in the Confluence native mobile app
	) {
		return null;
	}

	return (
		<ErrorBoundary attribution={Attribution.BACKBONE}>
			<Subscribe to={[BannerStateContainer]}>
				{(bannerState: BannerStateContainer) => {
					bannerState.show(BANNER_NAME, BANNER_HEIGHT);
					addUFOCustomData({
						isUnsupportedBrowserBannerDisplayed: true,
					});
					return (
						<UnsupportedBrowserBannerComponent
							bannerState={bannerState}
							setIsBannerClosed={setIsBannerClosed}
							isBannerClosed={isBannerClosed}
						/>
					);
				}}
			</Subscribe>
		</ErrorBoundary>
	);
};
